import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { config } from '../config/environment';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getPendingCount } from '../api/reviewsApi';
import {
  IconActivity,
  IconAnalytics,
  IconCoupons,
  IconDashboard,
  IconFileText,
  IconInventory,
  IconInvoice,
  IconLogout,
  IconMenu,
  IconMiscCosts,
  IconMoon,
  IconOrders,
  IconPackaging,
  IconPricing,
  IconProducts,
  IconSun,
  IconUsers,
  IconWaitlist,
  IconWallet,
  IconBroadcast,
  IconMessage,
  IconMessageSquare,
  IconStar,
  IconX,
} from './icons';

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: IconDashboard },
  { to: '/analytics', label: 'Analytics', icon: IconAnalytics },
  { to: '/products', label: 'Products', icon: IconProducts },
  { to: '/reviews', label: 'Reviews', icon: IconStar },
  { to: '/testimonials', label: 'Testimonials', icon: IconMessageSquare },
  { to: '/blog', label: 'Blog', icon: IconFileText },
  { to: '/pricing', label: 'Pricing', icon: IconPricing },
  { to: '/orders', label: 'Orders', icon: IconOrders },
  { to: '/inventory', label: 'Inventory', icon: IconInventory },
  { to: '/packaging', label: 'Packaging', icon: IconPackaging },
  { to: '/misc-costs', label: 'Misc Costs', icon: IconMiscCosts },
  { to: '/invoices', label: 'Invoices', icon: IconInvoice },
  { to: '/coupons', label: 'Coupons', icon: IconCoupons },
  { to: '/wallet', label: 'Wallet', icon: IconWallet },
  { to: '/users', label: 'Users', icon: IconUsers },
  { to: '/waitlist', label: 'Waitlist', icon: IconWaitlist },
  { to: '/broadcasts', label: 'Broadcasts', icon: IconBroadcast },
  { to: '/whatsapp-messages', label: 'WA Messages', icon: IconMessage },
  { to: '/api-logs', label: 'API Logs', icon: IconActivity },
];

function Sidebar({ onNavigate }: { onNavigate: () => void }) {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Reviews waiting for moderation, badged on the Reviews item so a queue
  // building up is visible from anywhere in the panel. Re-read on navigation,
  // plus a slow poll — approving a review doesn't change the route, so without
  // the timer the badge would sit stale on the very page you moderate from.
  const [pending, setPending] = useState(0);
  useEffect(() => {
    let alive = true;
    const read = () => getPendingCount().then((n) => { if (alive) setPending(n) });
    read();
    const t = setInterval(read, 60_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-3 px-5 py-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold text-sm font-bold text-[#1a1408]">
          TPC
        </div>
        <div>
          <p className="text-sm font-semibold leading-tight">TPC Admin</p>
          <p className="text-xs text-sub">The Perfume Culture</p>
        </div>
      </div>

      {/* min-h-0 is what makes this scroll: a flex child's default min-height is
          its content, so without it the nav refuses to shrink, grows past the
          fixed sidebar and the last items are simply cut off instead of
          scrolling. overscroll-contain stops a flick at the end of the list from
          scrolling the page behind it. */}
      <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain px-3 pb-2">
        {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? 'bg-gold/10 text-gold' : 'text-sub hover:bg-edge/40 hover:text-ink'
              }`
            }
          >
            <Icon className="h-5 w-5" />
            {label}
            {/* Only the Reviews item carries a count, and only while there is
                something to act on — a zero badge is just noise. */}
            {to === '/reviews' && pending > 0 && (
              <span
                className="ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-bad px-1.5 text-xs font-semibold text-white"
                title={`${pending} review${pending === 1 ? '' : 's'} awaiting moderation`}
              >
                {pending}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="shrink-0 space-y-1 border-t border-edge p-3">
        <button
          onClick={toggleTheme}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-sub transition-colors hover:bg-edge/40 hover:text-ink"
        >
          {isDark ? <IconSun className="h-5 w-5" /> : <IconMoon className="h-5 w-5" />}
          {isDark ? 'Light mode' : 'Dark mode'}
        </button>
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-sub transition-colors hover:bg-edge/40 hover:text-bad"
        >
          <IconLogout className="h-5 w-5" />
          Logout
        </button>
        {user?.email && (
          <p className="truncate px-3 pb-1 pt-2 text-xs text-sub" title={user.email}>
            {user.email}
          </p>
        )}
      </div>
    </div>
  );
}

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const close = () => setMobileOpen(false);
  const bannerVisible = !config.isProduction;

  return (
    <div className={`min-h-screen ${bannerVisible ? 'pt-7' : ''}`}>
      {/* Environment banner — hidden on production */}
      {bannerVisible && (
        <div
          className={`fixed inset-x-0 top-0 z-[60] flex h-7 items-center justify-center px-4 text-xs font-medium ${
            config.isUAT ? 'bg-yellow-500 text-yellow-950' : 'bg-blue-500 text-white'
          }`}
        >
          <span className="truncate">
            {config.isUAT
              ? '⚠️ UAT Environment — Test data only. Changes here do not affect production.'
              : '🔧 Development Environment — localhost'}
          </span>
        </div>
      )}

      {/* Mobile top bar */}
      <header
        className={`sticky z-30 flex h-14 items-center gap-3 border-b border-edge bg-surface px-4 md:hidden ${
          bannerVisible ? 'top-7' : 'top-0'
        }`}
      >
        <button onClick={() => setMobileOpen(true)} className="btn-ghost !p-2" aria-label="Open menu">
          <IconMenu className="h-5 w-5" />
        </button>
        <span className="text-sm font-semibold">TPC Admin</span>
      </header>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={close} aria-hidden="true" />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed bottom-0 left-0 z-50 w-64 transform border-r border-edge bg-surface transition-transform duration-200 md:translate-x-0 ${
          bannerVisible ? 'top-7' : 'top-0'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {mobileOpen && (
          <button
            onClick={close}
            className="btn-ghost absolute right-3 top-3 !p-1.5 md:hidden"
            aria-label="Close menu"
          >
            <IconX className="h-4 w-4" />
          </button>
        )}
        <Sidebar onNavigate={close} />
      </aside>

      {/* Main content */}
      <main className="md:pl-64">
        <div className="mx-auto max-w-7xl p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
